import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { warningAlert } from "../utils/messages";
import InventoryProduct, {
  MINIMUNAMOUNTPERSIZE,
} from "../../models/inventory-product";
import { getAbsoluteUrl } from "../../api/urls";

import { INVENTORY_PRODUCTS_RETRIEVE } from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";

import Category from "../../models/category";
import CategorySelect from "../category/CategorySelect";
import "./custom-style.css";
import { standardSizes } from "../../utils/constants/sizes";
import BrandLine from "../product-brand/BrandLine";

const ITEMS_PER_PAGE = 25;

interface FieldEntry {
  key: string;
  _classes: string[];
  label: string;
  filter?: boolean;
}

const FullInventoryProductsList = () => {
  const [search, setSearch] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [inventoryProducts, setInventoryProducts] = useState<
    InventoryProduct[]
  >([]);
  const [category, setCategory] = useState<Category | undefined>(undefined);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [maxCols, setMaxCols] = useState(0);

  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let categoryId = urlParams.get("category_id")
      ? urlParams.get("category_id")
      : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    if (categoryId !== "") map.set("category_id", categoryId);
    return map;
  };

  const fetchInventoryProducts = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const inventoryLocationsStatus = await getList<InventoryProduct>(
      "/inventory_products/all_locations/",
      limit,
      offset,
      urlParams
    );
    if (inventoryLocationsStatus.status === SUCCESS) {
      if (inventoryLocationsStatus.data !== undefined) {
        if (
          inventoryLocationsStatus.data.count === 0 &&
          inventoryLocationsStatus.data.items.length === 0
        ) {
          setTotalPages(1);
          setCurrentPage(1);
          setInventoryProducts([]);
          setLoading(false);
          return;
        }
        const count = inventoryLocationsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);

        setCurrentPage(urlParams.get("page"));

        setMaxCols(getMaxSizes(inventoryLocationsStatus.data.items));

        const newSizesFields: FieldEntry[] = [];
        const encounteredSizes = new Set();

        inventoryLocationsStatus.data.items.forEach((entry) => {
          entry.sizes?.forEach((size) => {
            if (
              typeof size.size === "string" &&
              !encounteredSizes.has(size.size)
            ) {
              const newFieldEntry = {
                key: size.size,
                _classes: ["text-center"],
                label: size.size,
                filter: false,
              };

              newSizesFields.push(newFieldEntry);
              encounteredSizes.add(size.size);
            }
          });
        });
        newSizesFields.sort((a, b) => {
          const keyLengthDiff = a.key.length - b.key.length;
          if (keyLengthDiff !== 0) {
            return keyLengthDiff;
          }
          return parseInt(a.key) - parseInt(b.key);
        });

        const flattenedInventoryProducts =
          inventoryLocationsStatus.data.items.map((entry) => {
            const flattenedProduct: { [key: string]: any } = { ...entry };
            newSizesFields?.forEach((size) => {
              if (typeof size.key === "string") {
                const sizeInfo = entry.sizes?.find(
                  (sizeinfo) => sizeinfo.size === size.key
                );
                if (sizeInfo) {
                  const difference =
                    sizeInfo.allocatedAmount !== undefined
                      ? sizeInfo.amount - sizeInfo.allocatedAmount
                      : sizeInfo.amount;
                  if (
                    sizeInfo.allocatedAmount &&
                    sizeInfo.allocatedAmount !== 0
                  ) {
                    flattenedProduct[
                      size.key
                    ] = `${sizeInfo.amount}(${sizeInfo.allocatedAmount})`;
                  } else {
                    flattenedProduct[size.key] = `${sizeInfo.amount}` || "0";
                  }
                  if (difference < MINIMUNAMOUNTPERSIZE) {
                    flattenedProduct[size.key] =
                      flattenedProduct[size.key] + "*";
                    flattenedProduct["hasMissingProducts"] = true;
                  }
                } else {
                  flattenedProduct[size.key] = "-";
                }
              }
            });
            return flattenedProduct;
          });
        setInventoryProducts(flattenedInventoryProducts);
      }
    } else {
      const message = inventoryLocationsStatus.detail
        ? inventoryLocationsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    if (previousParams.get("category_id")) {
      urlParams.set("category_id", previousParams.get("category_id"));
    }
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const getMaxSizes = (inventoryProducts: InventoryProduct[]): number => {
    if (!inventoryProducts || inventoryProducts.length === 0) {
      return 0;
    }

    return inventoryProducts.reduce((maxSizes, product) => {
      const sizeCount = product.sizes?.length || 0;
      return Math.max(maxSizes, sizeCount);
    }, 0);
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onCategoryChange = (newCategory: Category | null) => {
    setCategory(newCategory !== null ? newCategory : undefined);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    if (category !== undefined) {
      urlParams.set("category_id", category.id!.toString());
    } else {
      urlParams.delete("category_id");
    }
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  const onReportClick = async () => {
    const serch_param = search !== "" ? `?search=${search}` : "";
    const categoryIdParam =
      category !== undefined ? `&category_id=${category.id}` : "";
    const pdfEndpoint =
      getAbsoluteUrl("/inventory_products/all_locations/pdf/") +
      serch_param +
      categoryIdParam;

    window.open(pdfEndpoint, "_blank");
  };

  useEffect(() => {
    setLoading(true);
    fetchInventoryProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("inventoryProductsFull")) {
        return;
      }
      setLoading(true);
      fetchInventoryProducts();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Inventario de Productos / Todos los Inventarios</h3>
              </div>
            </CCardHeader>
            <CCardBody>
              <PermissionRequiredComponent
                permissionName={INVENTORY_PRODUCTS_RETRIEVE}
              >
                <CRow>
                  <CCol>
                    <CButton
                      color="danger"
                      className="fa-solid fa-file-pdf"
                      onClick={onReportClick}
                      disabled={loading}
                    ></CButton>
                  </CCol>
                </CRow>
              </PermissionRequiredComponent>
              <CRow>
                <CCol>
                  <h5>Filtros</h5>
                </CCol>
              </CRow>
              <CRow>
                <CCol></CCol>
              </CRow>
              <CRow className={"mt-2"}>
                <CCol md={1}>
                  <CLabel className={"mt-2"}>Categoría:</CLabel>
                </CCol>
                <CCol md={11}>
                  <CategorySelect
                    value={category ? category : null}
                    onChange={onCategoryChange}
                  ></CategorySelect>
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                      disabled={loading}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <div className="table-container-tall">
                  <div className="position-relative table-responsive">
                    <table className="table table-striped table-bordered table-fixed table-fixed-custom">
                      <tbody>
                        {inventoryProducts.map((item, index) => {
                          const sortedSizes = item.sizes?.sort((a, b) => {
                            // Check if both sizes are in the standard sizes list
                            const aIndex = standardSizes.indexOf(a.size);
                            const bIndex = standardSizes.indexOf(b.size);

                            // If both sizes are found in the standard sizes list, sort by their order in the list
                            if (aIndex !== -1 && bIndex !== -1) {
                              return aIndex - bIndex;
                            }

                            // If only one of the sizes is in the standard sizes list, prioritize the one that is
                            if (aIndex !== -1) return -1; // a comes first
                            if (bIndex !== -1) return 1; // b comes first

                            // Otherwise, sort them by string length and numeric value as before
                            const keyLengthDiff = a.size.length - b.size.length;
                            if (keyLengthDiff !== 0) {
                              return keyLengthDiff;
                            }

                            return parseInt(a.size) - parseInt(b.size);
                          });

                          const previousSizes =
                            index > 0
                              ? inventoryProducts[index - 1].sizes?.sort(
                                  (a, b) => {
                                    const keyLengthDiff =
                                      a.size.length - b.size.length;
                                    if (keyLengthDiff !== 0) {
                                      return keyLengthDiff;
                                    }
                                    return parseInt(a.size) - parseInt(b.size);
                                  }
                                )
                              : null;

                          const sizesMatch =
                            previousSizes &&
                            sortedSizes &&
                            previousSizes.length === sortedSizes.length &&
                            previousSizes.every(
                              (prev, idx) =>
                                prev.size === sortedSizes[idx]?.size
                            );

                          return (
                            <>
                              {!sizesMatch && (
                                <tr>
                                  <th className="text-center">Código</th>
                                  <th className="text-center">Nombre</th>
                                  <th className="text-center">Color</th>
                                  <th className="text-center">
                                    Cantidad Total
                                  </th>
                                  {Array.from({ length: maxCols }).map(
                                    (_, colIndex) => {
                                      const size = sortedSizes?.[colIndex];
                                      return (
                                        <td
                                          key={`data-${index}-${colIndex}`}
                                          className="text-center"
                                          style={{ fontWeight: "bold" }}
                                        >
                                          {size ? size.size : "-"}
                                        </td>
                                      );
                                    }
                                  )}
                                </tr>
                              )}
                              <tr>
                                <td className="text-center">
                                  {item.codeIdentifier}
                                </td>
                                <td className="text-center">
                                  <b>{item.name}</b>
                                  <BrandLine brandName={item.brandName} />
                                </td>
                                <td className="text-center">{item.color}</td>
                                <td className="text-center">
                                  <b>{item.totalAmount}</b>
                                </td>
                                {Array.from({ length: maxCols }).map(
                                  (_, colIndex) => {
                                    const size = sortedSizes?.[colIndex];
                                    let renderData: string = "";

                                    renderData = `${size?.amount || 0}` || "0";

                                    return (
                                      <td
                                        key={`data-${index}-${colIndex}`}
                                        className="text-center"
                                      >
                                        {size ? renderData : "-"}
                                      </td>
                                    );
                                  }
                                )}
                              </tr>
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="d-flex justify-content-center">
                  {!loading ? (
                    <CPagination
                      pages={totalPages}
                      activePage={currentPage}
                      onActivePageChange={(i: number) => onPageChange(i)}
                      className={totalPages < 2 ? "d-none" : "mt-2"}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </>
  );
};

export default FullInventoryProductsList;
